import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Camera, Brush, PackageCheck, Star, Gift, Users, MapPin, PartyPopper, ArrowRight } from "lucide-react";
import { IMAGES } from "@/constants/images";
import { useSettings } from "@/context/SettingsContext";
import ContactForm from "@/components/ContactForm";
import LeadCaptureForm from "@/components/LeadCaptureForm";
import HeroGallery from "@/components/HeroGallery";

const pop = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 20 } } };

const steps = [
  { icon: Camera, title: "Send your photos", text: "Upload a pic (or a few) of your favorite humans.", color: "bg-sky" },
  { icon: Brush, title: "We hand-draw it", text: "A one-of-a-kind, hilariously flattering caricature.", color: "bg-bubble" },
  { icon: PackageCheck, title: "You get the goods", text: "Delivered your way — by email, mailed to you, or both.", color: "bg-tangerine" },
];

const prices = [
  { name: "Single Person", price: 25, blurb: "One awesome subject." },
  { name: "Couple", price: 40, blurb: "Two, twice the fun." },
  { name: "Family / Group", price: 60, blurb: "3–4 people, +$10 each extra." },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const HERO_ROT = ["-6deg", "4deg", "-3deg"];
const absUrl = (u) => (u && u.startsWith("http") ? u : `${BACKEND}${u}`);

export default function Home() {
  const { settings } = useSettings();
  const heroList = (settings?.hero_images && settings.hero_images.filter(Boolean).length)
    ? settings.hero_images.filter(Boolean).slice(0, 3).map((u, i) => ({ url: absUrl(u), rot: HERO_ROT[i] || "0deg" }))
    : IMAGES.hero;
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-5 pt-12 pb-8 grid lg:grid-cols-2 gap-10 items-center">
        <motion.div initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.12 } } }}>
          <motion.span variants={pop} className="tag-pill bg-sun mb-5" data-testid="grand-forks-badge">
            <Sparkles className="w-4 h-4" /> Grand Forks' own Caricature artist!!
          </motion.span>
          <motion.h1 variants={pop} className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Turn your face into a <span className="text-bubble">masterpiece</span> of goofy.
          </motion.h1>
          <motion.p variants={pop} className="mt-5 text-lg text-ink/70 max-w-md">
            {settings?.tagline || "Hand-drawn caricatures for birthdays, weddings, gifts & just because."}
          </motion.p>
          <motion.p variants={pop} className="mt-2 flex items-center gap-1.5 font-display font-semibold text-ink/70" data-testid="hero-location">
            <MapPin className="w-4 h-4 text-bubble" /> Proudly based in Grand Forks &amp; Fargo, ND
          </motion.p>
          <motion.div variants={pop} className="mt-8 flex flex-wrap gap-3">
            <Link to="/order" className="brut-btn bg-bubble text-white text-lg" data-testid="hero-order-btn">
              Order your caricature <Brush className="w-5 h-5" />
            </Link>
            <Link to="/gallery" className="brut-btn bg-white text-lg" data-testid="hero-gallery-btn">
              See examples
            </Link>
          </motion.div>
          <motion.div variants={pop} className="mt-6 flex items-center gap-2 text-sm font-bold">
            <Gift className="w-4 h-4 text-tangerine" />
            <span>Get <span className="text-tangerine">10% off</span> when you're referred by a past customer!</span>
          </motion.div>
        </motion.div>

        <div className="relative h-[440px] sm:h-[520px]">
          <HeroGallery fallback={heroList.map((h) => h.url)} />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-5 py-12">
        <h2 className="font-display font-bold text-3xl sm:text-4xl mb-2">How it works</h2>
        <p className="text-ink/60 mb-8 text-lg">Three easy steps from selfie to art.</p>
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.1, type: "spring", stiffness: 250, damping: 20 }}
              className="brut-card p-6">
              <span className={`grid place-items-center w-14 h-14 rounded-2xl border-[3px] border-ink ${s.color} shadow-brut mb-4`}>
                <s.icon className="w-7 h-7 text-white" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-bold text-xl">{s.title}</h3>
              <p className="text-ink/70 mt-1">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* VIDEO TEASER */}
      <section className="max-w-7xl mx-auto px-5 py-8">
        <Link to="/process" data-testid="home-video-teaser"
          className="group block brut-card p-4 sm:p-6 bg-grape/15 hover:-translate-y-1 transition-transform">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div className="relative rounded-2xl overflow-hidden border-[3px] border-ink shadow-brut bg-cream-solid rotate-1 group-hover:rotate-0 transition-transform">
              <video
                autoPlay muted loop playsInline
                poster="/media/step4.jpg"
                className="w-full h-56 sm:h-64 object-cover block"
                data-testid="teaser-video"
              >
                <source src="/media/progression.webm" type="video/webm" />
                <source src="/media/progression.mp4" type="video/mp4" />
              </video>
              <span className="absolute top-3 left-3 tag-pill bg-sun">
                <Sparkles className="w-3.5 h-3.5" /> 13-second timelapse
              </span>
            </div>
            <div>
              <span className="font-accent text-3xl text-tangerine">watch the magic</span>
              <h2 className="font-display font-bold text-3xl sm:text-4xl mt-1">
                See a caricature come <span className="text-bubble">to life</span>
              </h2>
              <p className="text-ink/70 mt-3 text-lg">
                Blank page to finished piece — every stroke drawn by hand. Peek behind the pen and see exactly how your art gets made.
              </p>
              <span className="brut-btn bg-bubble text-white mt-6 inline-flex">
                See how it's made <ArrowRight className="w-5 h-5" />
              </span>
            </div>
          </div>
        </Link>
      </section>

      {/* EVENTS BANNER */}
      <section className="max-w-7xl mx-auto px-5 py-6">
        <Link to="/events" data-testid="events-banner"
          className="block brut-card p-6 sm:p-8 bg-bubble text-white hover:-translate-y-1 transition-transform">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className="grid place-items-center w-14 h-14 rounded-2xl border-[3px] border-ink bg-sun shadow-brut shrink-0">
                <PartyPopper className="w-7 h-7 text-ink" strokeWidth={2.4} />
              </span>
              <div>
                <h2 className="font-display font-bold text-2xl sm:text-3xl">Book me for your event!</h2>
                <p className="text-white/85 text-base">Live caricatures at weddings & parties — $180/hr, 2-hour minimum.</p>
              </div>
            </div>
            <span className="brut-btn bg-white text-ink text-lg shrink-0">See event pricing <ArrowRight className="w-5 h-5" /></span>
          </div>
        </Link>
      </section>

      {/* PRICING */}
      <section className="max-w-7xl mx-auto px-5 py-12">
        <div className="brut-card p-8 bg-cream-solid">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <h2 className="font-display font-bold text-3xl sm:text-4xl">Simple pricing</h2>
              <p className="text-ink/60 text-lg">No surprises. Just good art.</p>
            </div>
            <Link to="/order" className="brut-btn bg-sky">Start an order</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {prices.map((p, i) => (
              <div key={p.name} className="brut border-ink bg-white p-6 shadow-brut hover:-translate-y-1 hover:shadow-brutlg transition-[transform,box-shadow]">
                <div className="flex items-center gap-2 mb-2"><Users className="w-5 h-5 text-bubble" /><span className="font-display font-bold text-lg">{p.name}</span></div>
                <div className="font-display font-bold text-4xl">${p.price}</div>
                <p className="text-ink/60 mt-2">{p.blurb}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT / ARTIST */}
      <section className="max-w-7xl mx-auto px-5 py-12 grid lg:grid-cols-2 gap-10 items-center">
        <div className="brut-card p-3 rotate-[-2deg]">
          <img src={settings?.artist_image ? absUrl(settings.artist_image) : IMAGES.artist} alt="artist" className="w-full h-80 object-cover rounded-2xl border-2 border-ink" />
        </div>
        <div>
          <span className="font-accent text-3xl text-tangerine">the human behind the pen</span>
          <h2 className="font-display font-bold text-3xl sm:text-4xl mt-1">{settings?.artist_heading || "Every line drawn by hand, with heart."}</h2>
          <p className="text-ink/70 mt-4 text-lg whitespace-pre-line">{settings?.artist_bio || "No filters, no AI shortcuts — just a real artist capturing what makes you you. Perfect for gifts that people actually keep forever."}</p>
          <div className="mt-6 flex items-center gap-2">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 fill-sun text-ink" />)}
            <span className="font-bold ml-2">Loved by happy humans</span>
          </div>
          <Link to="/testimonials" className="brut-btn bg-white mt-6 inline-flex">Read reviews</Link>
        </div>
      </section>

      {/* LEAD CAPTURE */}
      <section className="max-w-3xl mx-auto px-5 py-6">
        <LeadCaptureForm />
      </section>

      {/* CONTACT */}
      <section className="max-w-3xl mx-auto px-5 py-12" id="contact">
        <ContactForm />
      </section>
    </div>
  );
}
